import { defineNuxtRouteMiddleware, useRuntimeConfig } from '#imports'

import { findRouteOrParentMatchingPredicate } from '@backmarket/utils/route/findRouteOrParentMatchingPredicate'

import { useRedirectLocale } from '../composables/useRedirectLocale'
import { useUserStore } from '../composables/useUserStore'

/**
 * The unauthenticated middleware.
 *
 * It makes sure that people are unauthenticated (non-authenticated).
 *
 * This middleware is globally available as soon as you register the OAuth
 * module in your application. You do not have to do anything to start
 * filtering page views.
 *
 * In order to prevent access to a page to authenticated people, you have
 * to provide some route meta configuration using the `meta.unauthenticated` property.
 *
 * Only the `required` value is mandatory. If set to `true`, access will be
 * prevented to authenticated people. If set to `false`, then everyone will
 * be able to access the page (both unauthenticated and authenticated users).
 *
 * @example
 * const route = {
 *   name: 'login',
 *   path: '/login',
 *   component: () => import('./components/Login'),
 *   meta: {
 *     unauthenticated: {
 *       required: true,
 *     },
 *   },
 * }
 *
 * If the access is prevented, the user will be redirected to the redirect
 * location configured when the module was instantiated. This is configured
 * via the `defaultRedirectLocationWhenAuthenticated` module option.
 *
 * However, it is possible to define a custom redirection location for a
 * specific route. This is achieved via the `meta.unauthenticated.redirection` property.
 *
 * @example
 * const route = {
 *   name: 'login',
 *   path: '/login',
 *   component: () => import('./components/Login'),
 *   meta: {
 *     authenticated: {
 *       required: true,
 *       redirection: { name: 'home' }
 *     },
 *   },
 * }
 */
export default defineNuxtRouteMiddleware(async (route) => {
  const config = useRuntimeConfig().public.oauth
  const { isUserFetched, isUserAuthenticated, fetchUser } = useUserStore()

  const matchedRouteWithUnauthenticated = findRouteOrParentMatchingPredicate(
    route,
    (routeDefinition) =>
      !!(routeDefinition.meta && routeDefinition.meta.unauthenticated),
  )

  // We may request a routes tree not protected at all.
  if (matchedRouteWithUnauthenticated === undefined) return
  if (matchedRouteWithUnauthenticated.meta.unauthenticated === undefined) return
  if (matchedRouteWithUnauthenticated.meta.unauthenticated.required === false)
    return

  // We don't want to fire the request each time the middleware is instanciated.
  // If the authentication status was previously fetched already, there is no
  // need to fetch it again, and we should use the data saved in our store.
  if (!isUserFetched.value) {
    await fetchUser()
  }

  // We're good to go, we're unauthenticated.
  if (!isUserAuthenticated.value) return

  const { getRouteLocationWithLocale } = useRedirectLocale()

  // We are expected to return different values based on the expected behaviour.
  // eslint-disable-next-line consistent-return
  return (
    matchedRouteWithUnauthenticated.meta.unauthenticated.redirection ||
    getRouteLocationWithLocale(config.defaultRedirectLocationWhenAuthenticated)
  )
})
